import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {useState} from 'react';
import {format} from 'date-fns';
import {useBugSynthesis} from '../hooks/useBugSynthesis';
import {useAppVersions} from '../hooks/useAppVersions';
import {useFilterOptions} from '../hooks/useFeedback';
import {ALL_TEAMS, TEAM_LABELS} from '../types/feedback';
import {prodVersionLabels, VersionList} from '../components/ProdVersions';
import {BUG_SEVERITY_COLORS, BUG_SEVERITY_LABELS} from '../types/bug';
import type {BugCandidate} from '../types/bug';
import {AffectedScope, toOccurrenceDate} from '../components/AffectedScope';
import {ContentSampleLine, ContentTags} from '../components/ContentTags';
import {applyVerificationTag, promoteCandidate, verifyCandidate} from '../services/bugService';
import {buildContentReport, checkBugInCode, ignoreSentryIssue, proposeReviewBugs, proposeSentryBugs, sendContentReportToSlack} from '../services/bugSynthesisService';
import type {
  CodeCheckResult,
  ContentReport,
  ReviewProposal,
  SentryCandidateInfo,
  SentryProposal,
} from '../services/bugSynthesisService';
import type {VerificationResult} from '../services/bugService';
import {ArrowSquareOut, Bug as BugIcon, Headset, Star} from '@phosphor-icons/react';
import {SentryLogo} from '../components/SentryLogo';

/** Resultat de la promotion d'un candidat, indexe par sa position dans la liste. */
type PromotionState =
    | {state: 'promoting'}
    | {state: 'done'; bugId: string; merged: boolean}
    | {state: 'error'; message: string};

type VerificationState =
    | {state: 'verifying'}
    | {state: 'done'; result: VerificationResult}
    | {state: 'error'; message: string};

const VERDICT_STYLE: Record<string, {label: string; className: string}> = {
  likely_real: {label: 'Evidence found', className: 'bg-emerald-100 text-emerald-800'},
  unsupported: {label: 'No evidence found', className: 'bg-amber-100 text-amber-800'},
  inconclusive: {label: 'Inconclusive', className: 'bg-gray-100 text-gray-600'},
};

/** Erreurs qui désignent un défaut de contenu sans tag `qcm.*`. */
const CONTENT_ERROR_LABELS: Record<string, string> = {
  empty_lesson_sections: 'leçons dont le contenu est introuvable',
};

/**
 * Y a-t-il de quoi bâtir un relevé pour l'équipe contenu ?
 *
 * Deux instrumentations, dont une seule pose des tags : s'en tenir aux tags
 * privait de relevé les issues qui ne nomment le contenu que dans leurs
 * données additionnelles.
 */
function hasContentClues(sentry: SentryCandidateInfo): boolean {
  return (sentry.contentTags?.length ?? 0) > 0
      || (sentry.contentErrorTypes?.length ?? 0) > 0;
}

const PRIMARY_COLOR = '#3ab3ff';
/** primary.light du thème. Le texte reste foncé : en blanc, le contraste
 *  tomberait à ~1,6:1, très en dessous du seuil d'accessibilité. */
const REPORT_COLOR = '#7dcfff';

export function BugSynthesisPage() {
    const t = useTraduire();
  const [statusFilter, setStatusFilter] = useState<'open' | 'new' | 'all'>('open');
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [teamFilter, setTeamFilter] = useState<string>('');
  const [promotions, setPromotions] = useState<Record<number, PromotionState>>({});
  const [verifications, setVerifications] = useState<Record<number, VerificationState>>({});
  const [taggings, setTaggings] = useState<Record<number, 'tagging' | 'done' | string>>({});
  const [sentry, setSentry] = useState<SentryProposal | 'loading' | string | null>(null);
  const [reviews, setReviews] = useState<ReviewProposal | 'loading' | string | null>(null);
  const [codeChecks, setCodeChecks] = useState<Record<string, CodeCheckResult | 'checking' | string>>({});
  const [reports, setReports] = useState<Record<string, ContentReport | 'building' | string>>({});
  const [copied, setCopied] = useState<string | null>(null);
  const [sent, setSent] = useState<Record<string, 'sending' | 'sent' | string>>({});

  const { versions: appVersions } = useAppVersions();
  const { platforms, loading: platformsLoading } = useFilterOptions();
  const { result, loading, error, generate } = useBugSynthesis();

  // Sans les versions relevées sur les stores : l'encadré décrit le filtre
  // appliqué par la fonction, et celle-ci ne lit que config/app_versions.
  const versionLabels = prodVersionLabels(appVersions);

  const handleGenerate = () => {
    // Une nouvelle analyse invalide les promotions affichees : les index ne
    // designent plus les memes candidats.
    setPromotions({});
    setVerifications({});
    generate({
      status: statusFilter,
      platform: platformFilter || undefined,
      team: teamFilter || undefined,
    });
  };

  const handlePromote = async (candidate: BugCandidate, index: number) => {
    setPromotions((p) => ({...p, [index]: {state: 'promoting'}}));
    try {
      const res = await promoteCandidate(candidate);
      setPromotions((p) => ({
        ...p,
        [index]: {state: 'done', bugId: res.bugId, merged: res.merged},
      }));
    } catch (e) {
      setPromotions((p) => ({
        ...p,
        [index]: {state: 'error', message: e instanceof Error ? e.message : 'Promotion failed'},
      }));
    }
  };

  const handleVerify = async (candidate: BugCandidate, index: number) => {
    setVerifications((v) => ({...v, [index]: {state: 'verifying'}}));
    try {
      const result = await verifyCandidate(candidate.feedbackIds);
      setVerifications((v) => ({...v, [index]: {state: 'done', result}}));
    } catch (e) {
      setVerifications((v) => ({
        ...v,
        [index]: {state: 'error', message: e instanceof Error ? e.message : 'Verification failed'},
      }));
    }
  };

  /**
   * Troisieme source : ce que les gens ecrivent publiquement.
   *
   * Sans version : la fonction prend la derniere publiee sur chaque boutique.
   * Google Play et Microsoft filtrent dessus ; l'App Store, qui n'attache pas
   * de version a ses avis, retombe sur la date de sortie.
   */
  const handleDetectReviews = async () => {
    setReviews('loading');
    try {
      setReviews(await proposeReviewBugs());
    } catch (e) {
      setReviews(e instanceof Error ? e.message : 'Analyse impossible');
    }
  };

  const handleDetectSentry = async () => {
    setSentry('loading');
    setPromotions({});
    try {
      setSentry(await proposeSentryBugs('14d'));
    } catch (e) {
      setSentry(e instanceof Error ? e.message : 'Détection impossible');
    }
  };

  const handleSendToSlack = async (shortId: string, report: string) => {
    setSent((p) => ({...p, [shortId]: 'sending'}));
    try {
      await sendContentReportToSlack(shortId, report);
      setSent((p) => ({...p, [shortId]: 'sent'}));
    } catch (e) {
      setSent((p) => ({...p, [shortId]: e instanceof Error ? e.message : 'Envoi impossible'}));
    }
  };

  const handleReport = async (shortId: string) => {
    setReports((p) => ({...p, [shortId]: 'building'}));
    try {
      setReports((p) => ({...p, [shortId]: 'building'}));
      const r = await buildContentReport(shortId);
      setReports((p) => ({...p, [shortId]: r}));
    } catch (e) {
      setReports((p) => ({...p, [shortId]: e instanceof Error ? e.message : 'Relevé impossible'}));
    }
  };

  const handleIgnore = async (shortId: string) => {
    const reason = window.prompt(
      `Pourquoi ${shortId} ne doit-elle plus être proposée ?`, '');
    if (reason === null || !reason.trim()) return;
    try {
      await ignoreSentryIssue(shortId, reason.trim());
      setSentry((p) => (p && typeof p === 'object'
        ? {...p, candidates: p.candidates.filter((c) => c.sentry.shortId !== shortId)}
        : p));
    } catch (e) {
      setCodeChecks((p) => ({...p, [shortId]: e instanceof Error ? e.message : 'Exclusion impossible'}));
    }
  };

  const handleCheckCode = async (shortId: string) => {
    setCodeChecks((p) => ({...p, [shortId]: 'checking'}));
    try {
      const r = await checkBugInCode(shortId);
      setCodeChecks((p) => ({...p, [shortId]: r}));
    } catch (e) {
      setCodeChecks((p) => ({...p, [shortId]: e instanceof Error ? e.message : 'Vérification impossible'}));
    }
  };

  const handleTagVerified = async (candidate: BugCandidate, index: number, result: VerificationResult) => {
    setTaggings((t) => ({...t, [index]: 'tagging'}));
    try {
      await applyVerificationTag({
        feedbackIds: candidate.feedbackIds,
        tag: 'verified',
        verdict: result.verdict,
        reasons: result.reasons,
        source: 'assisted',
      });
      setTaggings((t) => ({...t, [index]: 'done'}));
    } catch (e) {
      setTaggings((t) => ({...t, [index]: e instanceof Error ? e.message : 'Tagging failed'}));
    }
  };

  const statusLabels: Record<string, string> = {
    open: 'New + Triaged + In Progress',
    new: 'New only',
    all: 'All statuses',
  };

  return (
    <div>
      {/* Page title */}
      <div className="flex justify-between items-center mb-6">
        <div>
            <h1 className="text-2xl font-medium text-gray-900 flex items-center gap-2">
                <BugIcon size={26} weight="regular" className="text-primary"/>
                Détecter
            </h1>
          <p className="text-sm text-gray-500 mt-1">
            Candidats proposés depuis les signalements et depuis les issues Sentry
          </p>
          {/* Perimetre de l'analyse : il tient au sous-titre, pas aux filtres.
              Ce n'est pas un reglage qu'on change, c'est une limite a connaitre
              avant de lire les resultats. */}
          {/* inline-flex : l'encadré épouse son texte au lieu d'occuper une
              largeur fixe. max-w-full le garde dans l'écran, et chaque couple
              plateforme/version reste insécable — une version coupée en deux
              (« Web 2026- / 07-30 ») se lit mal et se recopie mal. */}
          <div className="inline-flex items-start gap-2 mt-3 px-3 py-2 max-w-full bg-blue-50 border border-blue-200 rounded-md">
            <svg className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span className="text-xs text-blue-700">
              Seuls les bugs de la <strong>{t(T.derniereVersion)}</strong> sont analysés
              {versionLabels.length > 0 && (
                <>
                  {' ('}<VersionList labels={versionLabels}/>{')'}
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Filters card */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-5 mb-6">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:gap-6 sm:items-end">
          {/* Status */}
          <div className="w-full sm:w-44">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t(T.colonneEtat)}</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'open' | 'new' | 'all')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm"
            >
              <option value="open">{t(T.nouveauxTriesEnCours)}</option>
              <option value="new">{t(T.nouveauxSeulement)}</option>
              <option value="all">{t(T.tousLesStatuts)}</option>
            </select>
          </div>

          {/* Platform */}
          <div className="w-full sm:w-36">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t(T.plateforme)}</label>
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              disabled={platformsLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm"
            >
              <option value="">{t(T.toutesLesPlateformes)}</option>
              {platforms.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Team */}
          <div className="w-full sm:w-36">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t(T.colonneEquipe)}</label>
            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm"
            >
              <option value="">{t(T.toutesLesEquipes)}</option>
              {ALL_TEAMS.map((equipe) => (
                <option key={equipe} value={equipe}>{t(TEAM_LABELS[equipe])}</option>
              ))}
            </select>
          </div>

          {/* Premiere source de detection : ce que les utilisateurs ecrivent.
              Poussees a droite : ce sont les actions, pas des filtres. */}
          <div className="w-full sm:w-auto sm:ml-auto">
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-pill shadow-bo text-sm font-semibold text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
              style={{backgroundColor: PRIMARY_COLOR}}
            >
              {loading ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                /* Le meme casque que le badge « support » de l'en-tete : les
                   feedbacks arrivent par ce canal. */
                <Headset size={18} weight="regular"/>
              )}
              {loading ? 'Détection…' : 'Détecter depuis les feedbacks'}
            </button>
          </div>

          {/* Seconde source de detection : les issues Sentry des versions
              publiees, sans signalement associe. */}
          <div className="w-full sm:w-auto">
            <button
              onClick={() => void handleDetectSentry()}
              disabled={sentry === 'loading'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-pill border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <SentryLogo size={17} className="text-[#362d59]"/>
              {sentry === 'loading' ? 'Détection…' : 'Détecter depuis Sentry'}
            </button>
          </div>

          {/* Troisieme source : les avis des boutiques. Elle dit ce que les
              deux autres ignorent — ce qui pousse quelqu'un a se plaindre
              publiquement plutot qu'a nous ecrire, et ce qui ne leve aucune
              exception : un audio muet, un bouton sans effet. */}
          <div className="w-full sm:w-auto">
            <button
              onClick={() => void handleDetectReviews()}
              disabled={reviews === 'loading'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-pill border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              title="Analyse les avis de la dernière version publiée sur les trois boutiques"
            >
              <Star size={17} weight="fill" className="text-amber-500"/>
              {reviews === 'loading' ? 'Analyse…' : 'Détecter depuis les avis'}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* State: Loading */}
      {loading && (
        <div className="bg-white rounded-lg shadow p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full animate-pulse"
                     style={{backgroundColor: PRIMARY_COLOR, animationDelay: '0ms'}}/>
                <div className="w-2 h-2 rounded-full animate-pulse"
                     style={{backgroundColor: PRIMARY_COLOR, animationDelay: '200ms'}}/>
                <div className="w-2 h-2 rounded-full animate-pulse"
                     style={{backgroundColor: PRIMARY_COLOR, animationDelay: '400ms'}}/>
            </div>
            <span className="text-sm font-medium text-gray-600">{t(T.analyseDesSignalements)}</span>
          </div>
          <div className="space-y-4">
            {[48, 100, 85, 66, 56, 100, 75, 40, 100, 85, 66].map((w, i) => (
              <div
                key={i}
                className="h-4 rounded animate-pulse bg-gray-200"
                style={{ width: `${w}%`, animationDelay: `${i * 100}ms` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pas d'état vide ici : la grande boîte « No synthesis generated yet »
          n'apprenait rien que les deux boutons ci-dessus ne disent déjà, et
          elle s'affichait au-dessus des candidats Sentry. Voir l'historique
          git si le besoin revient. */}

      {/* Candidats detectes depuis les avis des boutiques */}
      {typeof reviews === 'string' && reviews !== 'loading' && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{reviews}</div>
      )}
      {reviews && typeof reviews === 'object' && (
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-3">
            <div className="text-sm text-gray-500">
              {reviews.candidates.length} candidat{reviews.candidates.length !== 1 ? 's' : ''} issu
              {reviews.candidates.length !== 1 ? 's' : ''} des avis
              {reviews.version && ` · v${reviews.version}`}
              {` · ${reviews.scanned} avis lu${reviews.scanned !== 1 ? 's' : ''}`}
            </div>
            <button
              onClick={() => setReviews(null)}
              className="self-start text-xs text-gray-400 hover:text-gray-600"
            >
              Masquer
            </button>
          </div>

          {reviews.candidates.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">
              Aucun avis ne décrit de défaut technique sur cette version.
              {/* C'est le resultat le plus frequent, et il ne veut pas dire que
                  l'analyse a echoue : la plupart des avis parlent de prix, de
                  contenu ou d'envie de fonctionnalites. */}
              <div className="mt-1 text-xs text-gray-400">
                {reviews.scanned} avis examinés — la plupart parlent de prix, de contenu
                ou de fonctionnalités souhaitées.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {reviews.candidates.map((candidate, i) => {
                const index = 20000 + i; // espace d'index distinct des deux autres sources
                const promotion = promotions[index];
                return (
                  <div key={index} className="bg-white rounded-lg shadow px-4 sm:px-5 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${BUG_SEVERITY_COLORS[candidate.severity]}`}>
                            {BUG_SEVERITY_LABELS[candidate.severity]}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700">
                            Avis · {candidate.reviews.length}
                          </span>
                          {candidate.platforms.map((p) => (
                            <span key={p} className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{p}</span>
                          ))}
                        </div>
                        <h3 className="font-medium text-gray-900">{candidate.title}</h3>
                        <p className="mt-1 text-sm text-gray-600">{candidate.rationale}</p>
                      </div>
                      <div className="shrink-0">
                        {promotion?.state === 'done' ? (
                          <span className="text-xs text-green-700">
                            {promotion.merged ? 'Rattaché' : 'Créé'}
                          </span>
                        ) : (
                          <button
                            onClick={() => void handlePromote(candidate, index)}
                            disabled={promotion?.state === 'promoting'}
                            className="text-xs px-3 py-1.5 rounded-pill border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {promotion?.state === 'promoting' ? 'Création…' : 'Créer le bug'}
                          </button>
                        )}
                        {promotion?.state === 'error' && (
                          <p className="mt-1 text-xs text-red-600">{promotion.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Les avis eux-memes, et non leur resume : un avis est
                        court, souvent traduit, jamais reproductible tel quel.
                        C'est sur piece qu'on juge s'il y a un bug. */}
                    <ul className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                      {candidate.reviews.map((r) => (
                        <li key={r.id} className="text-xs text-gray-600">
                          <span className="text-gray-400">
                            {r.store} · {r.rating}★
                            {r.locale ? ` · ${r.locale}` : ''}
                            {r.appVersion ? ` · v${r.appVersion}${r.versionKnown ? '' : ' (déduite de la date)'}` : ''}
                          </span>
                          <span className="block text-gray-700">
                            {r.title ? `${r.title} — ` : ''}{r.body}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Candidats detectes depuis Sentry seul */}
      {typeof sentry === 'string' && sentry !== 'loading' && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{sentry}</div>
      )}
      {sentry && typeof sentry === 'object' && (
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-3">
            <div className="text-sm text-gray-500">
              {sentry.candidates.length} candidat{sentry.candidates.length !== 1 ? 's' : ''} Sentry
              {sentry.versions.length > 0 && ` · v${sentry.versions.join(', v')}`}
              {` · ${sentry.scanned} issue${sentry.scanned !== 1 ? 's' : ''} examinée${sentry.scanned !== 1 ? 's' : ''}`}
            </div>
            <button
              onClick={() => setSentry(null)}
              className="self-start text-xs text-gray-400 hover:text-gray-600"
            >
              Masquer
            </button>
          </div>

          {/* Ce qui a été écarté doit rester visible : une liste courte sans
              explication laisse croire qu'il n'y avait rien d'autre. */}
          <p className="text-xs text-gray-400 mb-3">
            Écartés : {sentry.excluded.byPriority} de priorité basse,{' '}
            {sentry.excluded.byCulprit} hors de notre code,{' '}
            {sentry.excluded.alreadyTracked} déjà rattachés à un bug,{' '}
            {sentry.excluded.alreadyFixedInCode ?? 0} déjà corrigés dans le code,{' '}
            {sentry.excluded.ignored ?? 0} exclus manuellement.
          </p>

          {sentry.candidates.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">
              Aucune issue Sentry ne remplit les critères sur les versions publiées.
            </div>
          ) : (
            <div className="space-y-3">
              {sentry.candidates.map((candidate, i) => {
                const index = 10000 + i; // espace d'index distinct des candidats feedback
                const promotion = promotions[index];
                return (
                  <div key={candidate.sentry.shortId} className="bg-white rounded-lg shadow px-4 sm:px-5 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${BUG_SEVERITY_COLORS[candidate.severity]}`}>
                            {BUG_SEVERITY_LABELS[candidate.severity]}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700">Sentry</span>
                          {/* Pourquoi ce candidat reste en Minor malgre son
                              nombre d'utilisateurs : l'app a signalé la
                              situation sans que rien casse pour eux. */}
                          {candidate.impactsUsers === false && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                              N'impacte pas les utilisateurs
                            </span>
                          )}
                          <h3 className="text-sm font-medium text-gray-900 break-words">{candidate.title}</h3>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{candidate.rationale}</p>
                        {/* Quel contenu est concerné : sans ça, une issue sur
                            178 utilisateurs ne dit pas quoi corriger. */}
                        {(candidate.sentry.contentErrorTypes?.length ?? 0) > 0 && (
                          <div className="text-xs mt-2">
                            <span className="text-gray-400">{t(T.contenu)}</span>
                            <span className="text-gray-700">
                              {candidate.sentry.contentErrorTypes!
                                .map((t) => CONTENT_ERROR_LABELS[t] || t)
                                .join(', ')}
                            </span>
                          </div>
                        )}
                        <div className="mt-2 space-y-1">
                          <ContentTags tags={candidate.sentry.contentTags}/>
                          <ContentSampleLine sample={candidate.sentry.latestContent}/>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                          <a href={candidate.sentry.url} target="_blank" rel="noreferrer"
                             className="text-purple-700 hover:underline">
                            {candidate.sentry.shortId}
                          </a>
                          {/* Qui est touche, et depuis quand : releve sur les tags
                              et les dates de l'issue, pas deduit du titre. */}
                          <AffectedScope
                            platforms={candidate.platforms}
                            versions={candidate.affectedVersions}
                            lastOccurrence={toOccurrenceDate(candidate.sentry.lastSeen)}
                          />
                          <span>{candidate.sentry.users} utilisateur(s)</span>
                          <span>{candidate.sentry.events} événement(s)</span>
                          <span className="text-amber-600">{t(T.aucunSignalement)}</span>
                          {candidate.sentry.culpritIsOurs === null && (
                            <span className="text-amber-600">{t(T.origineAVerifier)}</span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-wrap items-center gap-2">
                        {hasContentClues(candidate.sentry)
                          && reports[candidate.sentry.shortId] !== 'building' && (
                          <button
                            onClick={() => void handleReport(candidate.sentry.shortId)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-gray-900 hover:opacity-90"
                            style={{backgroundColor: REPORT_COLOR}}
                          >
                            Signaler à Assimil
                          </button>
                        )}
                        {reports[candidate.sentry.shortId] === 'building' && (
                          <span className="text-sm text-gray-400">{t(T.releveEnCours)}</span>
                        )}
                        {!promotion && codeChecks[candidate.sentry.shortId] !== 'checking' && (
                          <button
                            onClick={() => void handleCheckCode(candidate.sentry.shortId)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-600 bg-white hover:bg-gray-50"
                          >
                            Vérifier dans le code
                          </button>
                        )}
                        {codeChecks[candidate.sentry.shortId] === 'checking' && (
                          <span className="text-sm text-gray-400">{t(T.lectureDuDepot)}</span>
                        )}
                        {!promotion && (
                          <button
                            onClick={() => void handleIgnore(candidate.sentry.shortId)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-500 bg-white hover:bg-gray-50"
                            title={t(T.nePlusProposer)}
                          >
                            Exclure
                          </button>
                        )}
                        {!promotion && (
                          <button
                            onClick={() => handlePromote(candidate, index)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-white hover:opacity-90"
                            style={{backgroundColor: PRIMARY_COLOR}}
                          >
                            Create bug
                          </button>
                        )}
                        {promotion?.state === 'promoting' && <span className="text-sm text-gray-400">{t(T.creation)}</span>}
                        {promotion?.state === 'done' && (
                          <span className="text-sm text-green-700">
                            {promotion.merged ? 'Merged into existing bug' : 'Bug created'}
                          </span>
                        )}
                        {promotion?.state === 'error' && (
                          <span className="text-sm text-red-600">{promotion.message}</span>
                        )}
                      </div>
                    </div>

                    {/* Relevé pour l'équipe contenu */}
                    {typeof reports[candidate.sentry.shortId] === 'string'
                      && reports[candidate.sentry.shortId] !== 'building' && (
                      <div className="mt-2 text-xs text-red-600">
                        {reports[candidate.sentry.shortId] as string}
                      </div>
                    )}
                    {typeof reports[candidate.sentry.shortId] === 'object' && (() => {
                      const rep = reports[candidate.sentry.shortId] as ContentReport;
                      return (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <span className="text-xs text-gray-500">
                              {rep.stats.methods} méthode(s), {rep.stats.items} phrase(s)
                              {rep.stats.truncated && ' · relevé tronqué, il peut en manquer'}
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  void navigator.clipboard.writeText(rep.report);
                                  setCopied(candidate.sentry.shortId);
                                  setTimeout(() => setCopied(null), 2000);
                                }}
                                className="px-2 py-1 rounded border border-gray-300 text-xs text-gray-600 bg-white hover:bg-gray-50"
                              >
                                {copied === candidate.sentry.shortId ? t(T.copie) : t(T.copier)}
                              </button>
                              {/* L'envoi n'est possible qu'ici, une fois le relevé
                                  affiché : on ne publie pas un texte que personne
                                  n'a vu. */}
                              {!sent[candidate.sentry.shortId] && (
                                <button
                                  onClick={() => void handleSendToSlack(candidate.sentry.shortId, rep.report)}
                                  className="px-2 py-1 rounded border border-gray-300 text-xs text-gray-700 bg-white hover:bg-gray-50"
                                >
                                  Envoyer sur Slack
                                </button>
                              )}
                              {sent[candidate.sentry.shortId] === 'sending' && (
                                <span className="text-xs text-gray-400">{t(T.envoiEnCours)}</span>
                              )}
                              {sent[candidate.sentry.shortId] === 'sent' && (
                                <span className="text-xs text-emerald-700">{t(T.publieSurSlack)}</span>
                              )}
                              {typeof sent[candidate.sentry.shortId] === 'string'
                                && !['sending', 'sent'].includes(sent[candidate.sentry.shortId] as string) && (
                                <span className="text-xs text-red-600">{sent[candidate.sentry.shortId]}</span>
                              )}
                            </div>
                          </div>
                          <pre className="whitespace-pre-wrap break-words text-xs text-gray-700 bg-gray-50 rounded p-3 max-h-80 overflow-auto">
                            {rep.report}
                          </pre>
                        </div>
                      );
                    })()}

                    {/* Le code a-t-il bougé depuis ? Sentry ne peut pas le dire. */}
                    {typeof codeChecks[candidate.sentry.shortId] === 'string'
                      && codeChecks[candidate.sentry.shortId] !== 'checking' && (
                      <div className="mt-2 text-xs text-red-600">
                        {codeChecks[candidate.sentry.shortId] as string}
                      </div>
                    )}
                    {typeof codeChecks[candidate.sentry.shortId] === 'object' && (() => {
                      const check = codeChecks[candidate.sentry.shortId] as CodeCheckResult;
                      const style: Record<string, {label: string; cls: string}> = {
                        fixed: {label: 'Déjà corrigé', cls: 'bg-emerald-100 text-emerald-800'},
                        maybe_fixed: {label: 'Peut-être corrigé', cls: 'bg-amber-100 text-amber-800'},
                        still_present: {label: 'Toujours dans le code', cls: 'bg-red-100 text-red-800'},
                        gone: {label: 'Fichier disparu', cls: 'bg-gray-100 text-gray-600'},
                        unknown: {label: 'Indéterminé', cls: 'bg-gray-100 text-gray-600'},
                      };
                      return (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${style[check.verdict].cls}`}>
                            {style[check.verdict].label}
                          </span>
                          <ul className="mt-2 space-y-1">
                            {check.reasons.map((r, k) => (
                              <li key={k} className="text-xs text-gray-600 flex gap-1.5">
                                <span className="text-gray-300">·</span><span>{r}</span>
                              </li>
                            ))}
                          </ul>
                          {check.commits.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {check.commits.map((c) => (
                                <div key={c.hash} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                  <a href={c.url} target="_blank" rel="noreferrer"
                                     className="text-xs font-mono text-primary hover:underline">{c.shortHash}</a>
                                  <span className="text-xs text-gray-400">{c.date}</span>
                                  <span className="text-xs text-gray-700">{c.message}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* State: Result */}
      {!loading && result && (
        <>
          {/* Aucun candidat : soit rien a analyser, soit rien qui merite un bug */}
          {result.candidates.length === 0 && (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t(T.aucunCandidat)}</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                {result.message
                  || (result.stats.bugCount > 0
                    ? `${result.stats.bugCount} report${result.stats.bugCount !== 1 ? 's' : ''} analyzed, none of which describes a distinct defect.`
                    : 'No bug reports found for the current app versions with the selected filters.')}
              </p>
            </div>
          )}

          {result.candidates.length > 0 && (
            <>
              {/* Meta info bar */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                  <span>
                    {result.stats.bugCount} report{result.stats.bugCount !== 1 ? 's' : ''}
                    {' → '}
                    {result.stats.candidateCount} candidate{result.stats.candidateCount !== 1 ? 's' : ''}
                  </span>
                  <span className="text-gray-300 hidden sm:inline">|</span>
                  <span>Generated {format(new Date(result.metadata.generatedAt), 'MMM d, yyyy \'at\' HH:mm')}</span>
                  <span className="text-gray-300 hidden sm:inline">|</span>
                  <span>
                    {result.metadata.filters.platform === 'all' ? 'All platforms' : result.metadata.filters.platform}
                    {' · '}
                    {statusLabels[result.metadata.filters.status] || result.metadata.filters.status}
                  </span>
                </div>
                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="self-start shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-600 bg-white hover:bg-gray-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  Relancer la détection
                </button>
              </div>

              {/* Rappel : rien n'est enregistre tant qu'un humain n'a pas promu */}
              <div className="flex items-start gap-2 px-4 py-3 mb-4 bg-amber-50 border border-amber-200 rounded-md">
                <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span className="text-xs text-amber-800">
                  These are proposals, not bugs. Nothing is stored until you promote a candidate.
                  Promoting twice the same Sentry issue attaches the reports to the existing bug
                  instead of creating a duplicate.
                </span>
              </div>

              {/* Liste des candidats */}
              <div className="space-y-4">
                {result.candidates.map((candidate, index) => {
                  const promotion = promotions[index];
                  const verification = verifications[index];
                  return (
                    <div key={index} className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="px-4 sm:px-5 py-4">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${BUG_SEVERITY_COLORS[candidate.severity]}`}>
                                {BUG_SEVERITY_LABELS[candidate.severity]}
                              </span>
                              <h3 className="text-sm font-medium text-gray-900 break-words">{candidate.title}</h3>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed">{candidate.rationale}</p>
                          </div>

                          <div className="shrink-0 flex flex-wrap items-center gap-2">
                            {!promotion && verification?.state !== 'verifying' && (
                              <button
                                onClick={() => handleVerify(candidate, index)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-600 bg-white hover:bg-gray-50"
                              >
                                {verification ? t(T.reverifier) : t(T.verifier)}
                              </button>
                            )}
                            {verification?.state === 'verifying' && (
                              <span className="text-sm text-gray-400">{t(T.verification)}</span>
                            )}
                            {!promotion && (
                              <button
                                onClick={() => handlePromote(candidate, index)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm text-white hover:opacity-90"
                                style={{backgroundColor: PRIMARY_COLOR}}
                              >
                                Create bug
                              </button>
                            )}
                            {promotion?.state === 'promoting' && (
                              <span className="text-sm text-gray-400">{t(T.creation)}</span>
                            )}
                            {promotion?.state === 'done' && (
                              <span className="inline-flex items-center gap-1.5 text-sm text-green-700">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                                </svg>
                                {promotion.merged ? 'Merged into existing bug' : 'Bug created'}
                              </span>
                            )}
                            {promotion?.state === 'error' && (
                              <span className="text-sm text-red-600">{promotion.message}</span>
                            )}
                          </div>
                        </div>

                        {/* Provenance : ce sur quoi le candidat s'appuie */}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-gray-500">
                          <AffectedScope
                            platforms={candidate.platforms}
                            versions={candidate.affectedVersions}
                            lastOccurrence={toOccurrenceDate(candidate.lastReportedAt)}
                          />
                          <span>
                            {candidate.feedbackIds.length} report{candidate.feedbackIds.length !== 1 ? 's' : ''}
                          </span>
                          {candidate.sentryShortIds.length === 0 && (
                            <span className="text-amber-600">{t(T.aucunIndiceSentry)}</span>
                          )}
                        </div>

                        {/* Liens vers les pieces. Le signalement s'ouvre dans un
                            autre onglet : on y repond au signalant sans perdre la
                            liste des candidats, qui n'est pas rejouable a
                            l'identique. */}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {candidate.feedbackIds.length > 0 && (
                            <span className="text-xs text-gray-400">{t(T.repondreAuSignalant)}</span>
                          )}
                          {candidate.feedbackIds.map((id) => (
                            <a
                              key={id}
                              href={`/feedback/${id}`}
                              target="_blank"
                              rel="noreferrer"
                              title={t(T.ouvrirLeSignalement)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-primary/20 bg-primary/5 text-xs text-primary hover:bg-primary/10"
                            >
                              #{id.slice(0, 8)}
                              <ArrowSquareOut size={12} weight="regular"/>
                            </a>
                          ))}
                          {candidate.sentryShortIds.map((shortId) => (
                            <a
                              key={shortId}
                              href={`https://holiwave.sentry.io/issues/?query=${encodeURIComponent(shortId)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="px-2 py-0.5 rounded bg-purple-50 text-xs text-purple-700 hover:bg-purple-100"
                            >
                              {shortId}
                            </a>
                          ))}
                        </div>

                        {/* Verification : le verdict n'a de valeur qu'accompagne
                            de ce sur quoi il repose. */}
                        {verification?.state === 'error' && (
                          <div className="mt-3 text-sm text-red-600">{verification.message}</div>
                        )}
                        {verification?.state === 'done' && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${VERDICT_STYLE[verification.result.verdict].className}`}>
                                {VERDICT_STYLE[verification.result.verdict].label}
                              </span>
                              <span className="text-xs text-gray-400">
                                checked {verification.result.checks.length} report{verification.result.checks.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <ul className="space-y-1">
                              {verification.result.reasons.map((reason, i) => (
                                <li key={i} className="text-xs text-gray-600 flex gap-1.5">
                                  <span className="text-gray-300">·</span>
                                  <span>{reason}</span>
                                </li>
                              ))}
                            </ul>
                            {/* Le tag n'est propose que sur un verdict concluant.
                                « not_reproduced » n'apparait pas ici : il atteste
                                une tentative de reproduction, qui se fait a la
                                main depuis la fiche du signalement. */}
                            {verification.result.verdict === 'likely_real' && (
                              <div className="mt-2">
                                {!taggings[index] && (
                                  <button
                                    onClick={() => handleTagVerified(candidate, index, verification.result)}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-emerald-300 rounded-md text-xs text-emerald-800 bg-emerald-50 hover:bg-emerald-100"
                                  >
                                    Tag {candidate.feedbackIds.length} report{candidate.feedbackIds.length !== 1 ? 's' : ''} as verified
                                  </button>
                                )}
                                {taggings[index] === 'tagging' && (
                                  <span className="text-xs text-gray-400">{t(T.marquage)}</span>
                                )}
                                {taggings[index] === 'done' && (
                                  <span className="text-xs text-emerald-700">{t(T.marqueVerifie)}</span>
                                )}
                                {typeof taggings[index] === 'string'
                                  && taggings[index] !== 'tagging'
                                  && taggings[index] !== 'done' && (
                                  <span className="text-xs text-red-600">{taggings[index]}</span>
                                )}
                              </div>
                            )}
                            {verification.result.checks.some((c) => c.sentryIssues.length > 0) && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {verification.result.checks.flatMap((c) => c.sentryIssues).map((issue) => (
                                  <a
                                    key={issue.shortId}
                                    href={issue.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="px-2 py-0.5 rounded bg-purple-50 text-xs text-purple-700 hover:bg-purple-100"
                                    title={issue.title}
                                  >
                                    {issue.shortId}
                                    <span className="text-purple-400"> · {issue.status}</span>
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 text-xs text-gray-400 text-right">
                Claude API{result.metadata.tokensUsed > 0 && ` · ~${result.metadata.tokensUsed.toLocaleString()} tokens`}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
